import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { string } from 'yup';
import TextField from '../TextField';
import translations from '../AddressForm/translations';
import CountrySelect from '../CountrySelect';
import { useFormContext } from '../Form';
import Grid from '@material-ui/core/Grid';
import { options as optionsApi } from '@afosto/contacts/api/Options';
import { integrations as integrationsApi } from '@afosto/apps/api/Integrations';
import useIntl from '@afosto/utils-react/intl/useIntl';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import useCountries from '@afosto/utils-react/useCountries';
import getValidatedAddress from '@afosto/utils/getValidatedAddress';

const showField = (name, allowed = [], excluded = []) =>
  allowed.includes(name) && !excluded.includes(name);

const AddressInput = ({ onfetchAddressRules, excludedFields, readOnly }) => {
  const intl = useIntl();
  const { watch, getValues, setValue } = useFormContext();
  const [allowedFields, setAllowedFields] = useState([]);
  const [isFetchingFieldOptions, setIsFetchingFieldOptions] = useState(false);
  const [hasSendcloudIntegration, setHasSendcloudIntegration] = useState(false);
  const rawCountries = useCountries();
  const countryValue = watch('country');
  const organisationValue = watch('organisation');
  const { value: countryCode = 'NL' } = countryValue || {};

  const countries = rawCountries.map(rawCountry => ({
    label: rawCountry.name,
    ...rawCountry,
  }));

  const fechtAddressRulesRef = useRef(onfetchAddressRules);
  const excludedFieldsRef = useRef(excludedFields);

  useEffect(() => {
    fechtAddressRulesRef.current = onfetchAddressRules;
    excludedFieldsRef.current = excludedFields;
  });

  useEffect(() => {
    const fetchAddressRules = async code => {
      setIsFetchingFieldOptions(true);
      const { current: fetchAddressRulesCallback } = fechtAddressRulesRef;
      const { current: excluded } = excludedFieldsRef;

      try {
        const rules = {};
        const response = await optionsApi.getRequiredAddressFields({
          countryCode: code,
        });
        const { data = {} } = response;
        const requiredFields = data.required ?? [];
        const allowedFieldsResponse = data.allowed ?? [];

        setAllowedFields(allowedFieldsResponse);

        if (showField('premise_number', allowedFieldsResponse, excluded)) {
          rules.premiseNumber = string().required();
        }

        if (showField('postal_code', allowedFieldsResponse, excluded)) {
          rules.postalCode = string().required();
        }

        if (showField('locality', allowedFieldsResponse, excluded)) {
          rules.locality = string().required();
        }

        if (showField('thoroughfare', allowedFieldsResponse, excluded)) {
          rules.thoroughfare = string().required();
        }

        if (showField('organisation', allowedFieldsResponse, excluded)) {
          rules.organisation = string();
        }

        if (showField('administrative_area', allowedFieldsResponse, excluded)) {
          rules.administrativeArea = string().required();
        }

        if (fetchAddressRulesCallback && typeof fetchAddressRulesCallback === 'function') {
          fetchAddressRulesCallback({
            requiredFields,
            rules,
            allowedFields: allowedFieldsResponse,
          });
        }
      } catch (err) {
        if (fetchAddressRulesCallback && typeof fetchAddressRulesCallback === 'function') {
          fetchAddressRulesCallback({});
        }
      } finally {
        setIsFetchingFieldOptions(false);
      }
    };

    if (countryCode && countryCode !== '') {
      fetchAddressRules(countryCode);
    }
  }, [countryCode]);

  const validateAddress = async () => {
    try {
      const {
        country: countryCodeValue,
        postalCode,
        locality,
        thoroughfare,
        premiseNumber,
        premiseNumberSuffix,
      } = getValues();
      const validatedAddress = await getValidatedAddress(countryCodeValue?.alpha3Code, {
        countryCode: countryCodeValue,
        postalCode,
        locality,
        thoroughfare,
        premiseNumber,
        premiseNumberSuffix,
      });

      if (validatedAddress?.address) {
        const {
          postal_code: validPostalCode,
          locality: validLocality,
          thoroughfare: validThoroughfare,
          premise_number: validPremiseNumber,
          premise_number_suffix: validPremiseNumberSuffix,
        } = validatedAddress.address;

        setValue('postalCode', validPostalCode);
        setValue('locality', validLocality);
        setValue('thoroughfare', validThoroughfare);
        setValue('premiseNumber', validPremiseNumber);
        setValue('premiseNumberSuffix', validPremiseNumberSuffix);
      }
    } catch (error) {
      // do something
    }
  };

  useEffect(() => {
    let isMounted = true;

    const checkSendCloudIntegration = async () => {
      try {
        const integrationsResponse = await integrationsApi.listIntegrations({
          isDeleted: 0,
          appCode: 'sendcloud',
        });

        if (integrationsResponse?.data?.length > 0 && isMounted) {
          setHasSendcloudIntegration(true);
        }
      } catch (error) {
        setHasSendcloudIntegration(false);
      }
    };

    checkSendCloudIntegration();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <LoadingWrapper isLoading={isFetchingFieldOptions}>
      {showField('name', allowedFields, excludedFields) && (
        <>
          {showField('given_name', ['given_name'], excludedFields) && (
            <TextField
              name="givenName"
              label={intl.formatMessage(translations.fields.given_name)}
              readOnly={readOnly}
            />
          )}

          {showField('additional_name', ['additional_name'], excludedFields) && (
            <TextField
              name="additionalName"
              label={intl.formatMessage(translations.fields.additional_name)}
              readOnly={readOnly}
            />
          )}

          {showField('family_name', ['family_name'], excludedFields) && (
            <TextField
              name="familyName"
              label={intl.formatMessage(translations.fields.family_name)}
              readOnly={readOnly}
            />
          )}
        </>
      )}

      {showField('organisation', allowedFields, excludedFields) && (
        <TextField
          name="organisation"
          label={intl.formatMessage(translations.fields.company)}
          readOnly={readOnly}
          helperText={
            hasSendcloudIntegration && organisationValue.length > 35
              ? intl.formatMessage(translations.helpText.sendCloudWarning, {
                  length: organisationValue.length,
                })
              : ''
          }
        />
      )}

      {showField('country', ['country'], excludedFields) && (
        <CountrySelect
          name="country"
          label={intl.formatMessage(translations.fields.country)}
          options={countries}
          disableClearable
          readOnly={readOnly}
          onBlur={validateAddress}
        />
      )}

      {['NL', 'BE'].includes(countryCode) &&
        showField('postal_code', allowedFields, excludedFields) && (
          <TextField
            name="postalCode"
            label={intl.formatMessage(translations.fields.postal_code)}
            readOnly={readOnly}
            onBlur={validateAddress}
          />
        )}

      {!['NL'].includes(countryCode) &&
        showField('thoroughfare', allowedFields, excludedFields) && (
          <TextField
            name="thoroughfare"
            label={intl.formatMessage(translations.fields.thoroughfare)}
            readOnly={readOnly}
            onBlur={validateAddress}
          />
        )}
      {showField('premise_number', allowedFields, excludedFields) && (
        <Grid container spacing={1} wrap="nowrap">
          <Grid item xs={7}>
            <TextField
              name="premiseNumber"
              label={intl.formatMessage(translations.fields.premise_number)}
              type="number"
              readOnly={readOnly}
              onBlur={validateAddress}
            />
          </Grid>
          {!excludedFields.includes('organisation') && (
            <Grid item xs={5}>
              <TextField
                name="premiseNumberSuffix"
                label={intl.formatMessage(translations.fields.premise_number_suffix)}
                readOnly={readOnly}
                onBlur={validateAddress}
              />
            </Grid>
          )}
        </Grid>
      )}

      {['NL'].includes(countryCode) && showField('thoroughfare', allowedFields, excludedFields) && (
        <TextField
          name="thoroughfare"
          label={intl.formatMessage(translations.fields.thoroughfare)}
          readOnly={readOnly}
          onBlur={validateAddress}
        />
      )}

      {showField('locality', allowedFields, excludedFields) && (
        <TextField
          name="locality"
          label={intl.formatMessage(translations.fields.locality)}
          readOnly={readOnly}
          onBlur={validateAddress}
        />
      )}

      {!['NL', 'BE'].includes(countryCode) &&
        showField('postal_code', allowedFields, excludedFields) && (
          <TextField
            name="postalCode"
            label={intl.formatMessage(translations.fields.postal_code)}
            readOnly={readOnly}
            onBlur={validateAddress}
          />
        )}

      {showField('administrative_area', allowedFields, excludedFields) && (
        <TextField
          name="administrativeArea"
          label={intl.formatMessage(translations.fields.administrative_area)}
          readOnly={readOnly}
        />
      )}
    </LoadingWrapper>
  );
};

AddressInput.propTypes = {
  excludedFields: PropTypes.array,
  onfetchAddressRules: PropTypes.func,
  readOnly: PropTypes.bool,
};

AddressInput.defaultProps = {
  excludedFields: [],
  onfetchAddressRules: undefined,
  readOnly: false,
};

export default AddressInput;
